import React, { useMemo, useRef, useCallback, useState, forwardRef } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import BaseHierarchy, { BaseHierarchyHandle, HierarchyItem, HierarchyLink, HierarchyPosition } from '../BaseHierarchy';
import { MermaidParser } from './MermaidParser';
import MermaidNodeRenderer from './MermaidNodeRenderer';
import MermaidLinkRenderer, { MermaidLinkRendererHandle } from './MermaidLinkRenderer';
import {
  MermaidHierarchyAdapterProps,
  MermaidHierarchyItem,
  MermaidHierarchyLink,
  ParsedMermaidDiagram,
  MermaidParseError,
} from './types';
import { validateMermaidSpec, formatParseError } from './utils';

export interface MermaidHierarchyAdapterHandle extends BaseHierarchyHandle {
  getParsedDiagram: () => ParsedMermaidDiagram | null;
  getParseErrors: () => MermaidParseError[];
}

/**
 * Adapter component that converts Mermaid.js specifications to BaseHierarchy format
 * Supports flowcharts, sequence diagrams, class diagrams, state diagrams, and ER diagrams
 */
const MermaidHierarchyAdapter = forwardRef<MermaidHierarchyAdapterHandle, MermaidHierarchyAdapterProps>(
  (props, ref) => {
    const {
      mermaidSpec,
      viewportSize,
      gridSize,
      initialZoom,
      minZoom,
      maxZoom,
      positioningStrategy = 'circular',
      centerSpacing = 400,
      horizontalOffset = 0,
      showGrid = false,
      showDebugInfo = __DEV__,
      backgroundColor,
      renderItem: customRenderItem,
      renderLinkHandler: customRenderLinkHandler,
      onItemPositionChange,
      onSnap,
      isLoading = false,
    } = props;

    const baseHierarchyRef = useRef<BaseHierarchyHandle>(null);
    const linkRendererRef = useRef<MermaidLinkRendererHandle>(null);
    const [parseErrors, setParseErrors] = useState<MermaidParseError[]>([]);
    const [parsedDiagram, setParsedDiagram] = useState<ParsedMermaidDiagram | null>(null);

    // Parse Mermaid specification
    const parseResult = useMemo(() => {
      try {
        // Validate specification format first
        const validation = validateMermaidSpec(mermaidSpec);
        if (!validation.valid) {
          const error: MermaidParseError = {
            message: validation.error || 'Invalid Mermaid specification',
          };
          setParseErrors([error]);
          setParsedDiagram(null);
          return null;
        }

        // Parse the diagram
        const diagram = MermaidParser.parse(mermaidSpec);
        const errors = MermaidParser.validate(diagram);

        if (errors.length > 0) {
          setParseErrors(errors);
          if (__DEV__) {
            console.warn('Mermaid parse warnings:', errors);
          }
          // Still return diagram if it has nodes, but with warnings
          if (diagram.nodes.length === 0) {
            setParsedDiagram(null);
            return null;
          }
        } else {
          setParseErrors([]);
        }

        setParsedDiagram(diagram);
        return diagram;
      } catch (error) {
        const parseError: MermaidParseError = {
          message: error instanceof Error ? error.message : 'Unknown parsing error',
          diagramType: 'unknown',
        };
        setParseErrors([parseError]);
        setParsedDiagram(null);
        if (__DEV__) {
          console.error('Mermaid parsing error:', error);
        }
        return null;
      }
    }, [mermaidSpec]);

    // Convert parsed nodes to HierarchyItem format
    const hierarchyItems = useMemo((): MermaidHierarchyItem[] => {
      if (!parseResult) return [];

      return parseResult.nodes.map((node) => ({
        _id: node.id,
        label: node.label,
        shape: node.shape || 'rectangle',
        mermaidType: node.type,
        style: node.style,
        class: node.class,
        originalId: node.id,
        // Include all original node properties
        ...node,
      }));
    }, [parseResult]);

    // Convert parsed edges to HierarchyLink format
    const hierarchyLinks = useMemo((): MermaidHierarchyLink[] => {
      if (!parseResult) return [];

      return parseResult.edges.map((edge) => ({
        sourceId: edge.sourceId,
        targetId: edge.targetId,
        type: edge.type || 'default',
        label: edge.label,
        edgeStyle: edge.style,
        color: undefined, // Will be determined by renderer
        width: 2,
        mermaidType: edge.type,
      }));
    }, [parseResult]);

    // Default renderItem function
    const defaultRenderItem = useCallback(
      (
        item: MermaidHierarchyItem,
        position: HierarchyPosition,
        onPositionChange: (position: HierarchyPosition) => void,
        zoomLevel?: number
      ) => {
        return (
          <MermaidNodeRenderer
            item={item}
            position={position}
            onPositionChange={onPositionChange}
            zoomLevel={zoomLevel}
            dragEnabled={true}
          />
        );
      },
      []
    );

    // Default renderLinkHandler function
    const defaultRenderLinkHandler = useCallback(
      (props: {
        itemPositionsRef: React.RefObject<Record<string, HierarchyPosition>>;
        links: MermaidHierarchyLink[];
        theme: boolean;
        zoomLevel: number;
      }) => {
        return (
          <MermaidLinkRenderer
            ref={linkRendererRef}
            itemPositionsRef={props.itemPositionsRef}
            links={props.links}
            theme={props.theme}
            zoomLevel={props.zoomLevel}
          />
        );
      },
      []
    );

    // Handle position changes
    const handleItemPositionChange = useCallback(
      (itemId: string, position: HierarchyPosition) => {
        // Force link renderer update when positions change
        // Use a small delay to ensure positions are updated in the ref first
        setTimeout(() => {
          linkRendererRef.current?.forceUpdate();
        }, 0);

        if (onItemPositionChange) {
          onItemPositionChange(itemId, position);
        }
      },
      [onItemPositionChange]
    );

    // Expose methods via ref
    React.useImperativeHandle(ref, () => {
      const baseMethods = baseHierarchyRef.current ? {
        setComponentPosition: baseHierarchyRef.current.setComponentPosition,
        getComponentPosition: baseHierarchyRef.current.getComponentPosition,
        centerAllComponents: baseHierarchyRef.current.centerAllComponents,
        moveTo: baseHierarchyRef.current.moveTo,
        zoomTo: baseHierarchyRef.current.zoomTo,
        zoomBy: baseHierarchyRef.current.zoomBy,
        getCurrentZoom: baseHierarchyRef.current.getCurrentZoom,
        getViewportSize: baseHierarchyRef.current.getViewportSize,
        forceUpdate: baseHierarchyRef.current.forceUpdate,
        getAllItemPositions: baseHierarchyRef.current.getAllItemPositions,
        centerView: baseHierarchyRef.current.centerView,
        centerOnItem: baseHierarchyRef.current.centerOnItem,
        centerOnPrimary: baseHierarchyRef.current.centerOnPrimary,
      } : {};
      
      return {
        ...baseMethods,
        getParsedDiagram: () => parsedDiagram,
        getParseErrors: () => parseErrors,
      };
    }, [parsedDiagram, parseErrors]);

    // Show error state if parsing failed
    if (parseErrors.length > 0 && !parseResult) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Mermaid Parsing Error</Text>
          {parseErrors.map((error, index) => (
            <Text key={index} style={styles.errorText}>
              {formatParseError(error)}
            </Text>
          ))}
          {showDebugInfo && (
            <View style={styles.debugContainer}>
              <Text style={styles.debugLabel}>Specification preview:</Text>
              <Text style={styles.debugText}>
                {mermaidSpec.substring(0, 200)}
                {mermaidSpec.length > 200 ? '...' : ''}
              </Text>
            </View>
          )}
        </View>
      );
    }

    // Show loading state
    if (isLoading || !parseResult) {
      return (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Parsing Mermaid diagram...</Text>
        </View>
      );
    }

    // Show empty state
    if (hierarchyItems.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No nodes found in Mermaid diagram</Text>
        </View>
      );
    }

    // Determine primary item (first node or node with specific characteristics)
    const primaryItem = useMemo(() => {
      // For flowcharts, look for a node named "Start" or the first node
      const startNode = hierarchyItems.find(
        (item) => item.label.toLowerCase().includes('start') || item.originalId.toLowerCase() === 'start'
      );
      return startNode || hierarchyItems[0];
    }, [hierarchyItems]);

    // Render with BaseHierarchy
    return (
      <BaseHierarchy
        ref={baseHierarchyRef}
        items={hierarchyItems}
        primaryItem={primaryItem}
        links={hierarchyLinks}
        isLoading={isLoading}
        onItemPositionChange={handleItemPositionChange}
        onSnap={onSnap}
        renderItem={customRenderItem || defaultRenderItem}
        renderLinkHandler={customRenderLinkHandler || defaultRenderLinkHandler}
        viewportSize={viewportSize}
        gridSize={gridSize}
        initialZoom={initialZoom}
        minZoom={minZoom}
        maxZoom={maxZoom}
        positioningStrategy={positioningStrategy}
        centerSpacing={centerSpacing}
        horizontalOffset={horizontalOffset}
        showGrid={showGrid}
        showDebugInfo={showDebugInfo}
        backgroundColor={backgroundColor}
      />
    );
  }
);

MermaidHierarchyAdapter.displayName = 'MermaidHierarchyAdapter';

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#FEE2E2',
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#DC2626',
    marginBottom: 10,
  },
  errorText: {
    fontSize: 14,
    color: '#991B1B',
    marginBottom: 5,
    textAlign: 'center',
  },
  debugContainer: {
    marginTop: 15,
    padding: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
  },
  debugLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#7F1D1D',
    marginBottom: 5,
  },
  debugText: {
    fontSize: 11,
    color: '#7F1D1D',
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'Courier',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#6B7280',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#9CA3AF',
  },
});

export default MermaidHierarchyAdapter;

